import React, { useState, useEffect } from 'react';
import { Play, Pause, Plus, Clock, CheckCircle, XCircle, AlertTriangle, Edit, Trash2, X, Calendar, FileText, Bot, Settings, RotateCcw, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import API from '../api';
import { useActiveOrg } from '../contexts/ActiveOrgContext';

const getShortenedPath = (path) => {
  if (!path) return '-';
  const parts = path.split(/[/\\]/);
  return parts[parts.length - 1];
};

const Jobs = () => {
  const { t, i18n } = useTranslation();
  const { activeOrg } = useActiveOrg();
  const [jobs, setJobs] = useState([]);
  const [robots, setRobots] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(null); // job object
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    department: '',
    script_path: '',
    robot: '',
    manual_time_minutes: 15,
    is_paused: false
  });
  const [scriptFile, setScriptFile] = useState(null); // Yeni iş için script dosyası

  // Zamanlama form state
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    schedule_type: 'once',
    run_at: '',
    run_time: '09:00',
    weekdays: [],
    monthly_option: 'day_of_month',
    day_of_month: 1,
    weekday_of_month: 0,
    repeat_interval: 60,
    cron_expression: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    is_active: true
  });

  const WEEKDAYS = [
    { value: 0, label: t('jobs.weekdays_short.mon', 'Pzt'), fullLabel: t('jobs.weekdays.mon', 'Pazartesi') },
    { value: 1, label: t('jobs.weekdays_short.tue', 'Sal'), fullLabel: t('jobs.weekdays.tue', 'Salı') },
    { value: 2, label: t('jobs.weekdays_short.wed', 'Çar'), fullLabel: t('jobs.weekdays.wed', 'Çarşamba') },
    { value: 3, label: t('jobs.weekdays_short.thu', 'Per'), fullLabel: t('jobs.weekdays.thu', 'Perşembe') },
    { value: 4, label: t('jobs.weekdays_short.fri', 'Cum'), fullLabel: t('jobs.weekdays.fri', 'Cuma') },
    { value: 5, label: t('jobs.weekdays_short.sat', 'Cmt'), fullLabel: t('jobs.weekdays.sat', 'Cumartesi') },
    { value: 6, label: t('jobs.weekdays_short.sun', 'Paz'), fullLabel: t('jobs.weekdays.sun', 'Pazar') },
  ];

  useEffect(() => {
    fetchData();
  }, [activeOrg]); // activeOrg değiştiğinde yeniden fetch

  const fetchData = async () => {
    try {
      const [jobsRes, robotsRes, meRes] = await Promise.all([
        API.get(`/jobs/?_t=${Date.now()}`),
        API.get(`/robots/?_t=${Date.now()}`),
        API.get('/me/').catch(() => ({ data: { is_superuser: false, is_staff: false } }))
      ]);

      // Organizasyona göre filtrele
      let filteredRobots = robotsRes.data;
      let filteredJobs = jobsRes.data;

      if (activeOrg) {
        // Önce robotları filtrele
        filteredRobots = robotsRes.data.filter(robot =>
          robot.organization === activeOrg.id || robot.organization_name === activeOrg.name
        );
        // Sonra o organizasyonun robotlarına ait job'ları filtrele
        const orgRobotIds = filteredRobots.map(r => r.id);
        filteredJobs = jobsRes.data.filter(job => orgRobotIds.includes(job.robot));
      }

      setJobs(filteredJobs);
      setRobots(filteredRobots);
      setCurrentUser(meRes.data);
      setLoading(false);
    } catch (err) {
      setError(t('jobs.error_fetch_jobs', 'İş listesi yüklenirken hata oluştu.'));
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const data = { ...formData };
      if (!data.robot) {
        setError(t('jobs.error_robot_required', 'Robot seçimi zorunludur.'));
        return;
      }

      let jobId;

      if (editingJob) {
        await API.patch(`/jobs/${editingJob.id}/`, data);
        jobId = editingJob.id;
        setSuccess(t('jobs.success_job_updated', 'İş güncellendi.'));
      } else {
        const res = await API.post('/jobs/', data);
        jobId = res.data.id;
        setSuccess(t('jobs.success_job_created', 'Yeni iş oluşturuldu.'));
      }

      // Script dosyası seçildiyse yükle
      if (scriptFile && jobId) {
        const scriptFormData = new FormData();
        scriptFormData.append('file', scriptFile);
        try {
          await API.post(`/jobs/${jobId}/upload_script/`, scriptFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSuccess(t('jobs.success_job_created_script_uploaded', 'İş oluşturuldu ve script yüklendi.'));
        } catch (uploadErr) {
          setError(`${t('jobs.error_job_created_script_failed', 'İş oluşturuldu ama script yüklenemedi:')} ${uploadErr.response?.data?.error || t('common.unknown_error', 'Bilinmeyen hata')}`);
        }
      }

      setShowModal(false);
      setEditingJob(null);
      setScriptFile(null);
      setFormData({ name: '', description: '', department: '', script_path: '', robot: '', manual_time_minutes: 15, is_paused: false });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || t('jobs.error_action_failed', 'İşlem başarısız oldu.'));
    }
  };

  const handleScheduleSubmit = async () => {
    try {
      const data = {
        job: showScheduleModal.id,
        name: scheduleForm.name,
        schedule_type: scheduleForm.schedule_type,
        run_time: scheduleForm.run_time,
        start_date: scheduleForm.start_date,
        is_active: scheduleForm.is_active
      };

      // end_date sadece dolu ise ekle
      if (scheduleForm.end_date) {
        data.end_date = scheduleForm.end_date;
      }

      // Schedule type'a göre gerekli alanları ekle
      if (scheduleForm.schedule_type === 'once') {
        data.run_at = scheduleForm.run_at;
      }
      if (scheduleForm.schedule_type === 'weekly') {
        data.weekdays = scheduleForm.weekdays;
      }
      if (scheduleForm.schedule_type === 'monthly') {
        data.monthly_option = scheduleForm.monthly_option;
        data.day_of_month = scheduleForm.day_of_month;
        data.weekday_of_month = scheduleForm.weekday_of_month;
      }
      if (scheduleForm.schedule_type === 'interval') {
        data.repeat_interval = scheduleForm.repeat_interval;
      }
      if (scheduleForm.schedule_type === 'cron') {
        data.cron_expression = scheduleForm.cron_expression;
      }

      await API.post('/schedules/', data);
      setSuccess(t('jobs.success_schedule_created', 'Zamanlama oluşturuldu.'));
      setShowScheduleModal(null);
      resetScheduleForm();
      fetchData();
    } catch (err) {
      console.error('Schedule error:', err.response?.data);
      setError(err.response?.data?.detail || JSON.stringify(err.response?.data) || t('jobs.error_schedule_failed', 'Zamanlama eklenemedi.'));
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (window.confirm(t('jobs.confirm_delete_schedule', 'Bu zamanlamayı silmek istediğinize emin misiniz?'))) {
      try {
        await API.delete(`/schedules/${scheduleId}/`);
        setSuccess(t('jobs.success_schedule_deleted', 'Zamanlama silindi.'));
        // Refetch and update modal
        const jobsRes = await API.get(`/jobs/?_t=${Date.now()}`);
        
        let filteredJobs = jobsRes.data;
        if (activeOrg) {
          const orgRobotIds = robots.map(r => r.id);
          filteredJobs = jobsRes.data.filter(job => orgRobotIds.includes(job.robot));
        }
        setJobs(filteredJobs);
        
        // Update the detail modal with fresh data
        if (showDetailModal) {
          const updatedJob = jobsRes.data.find(j => j.id === showDetailModal.id);
          if (updatedJob) setShowDetailModal(updatedJob);
        }
      } catch (err) {
        setError(t('jobs.error_schedule_delete_failed', 'Zamanlama silinemedi.'));
      }
    }
  };

  const handleToggleSchedule = async (schedule) => {
    try {
      await API.patch(`/schedules/${schedule.id}/`, { is_active: !schedule.is_active });
      setSuccess(schedule.is_active ? t('jobs.success_schedule_deactivated', 'Zamanlama pasif yapıldı.') : t('jobs.success_schedule_activated', 'Zamanlama aktif edildi.'));
      // Refetch and update modal
      const jobsRes = await API.get(`/jobs/?_t=${Date.now()}`);
      
      let filteredJobs = jobsRes.data;
      if (activeOrg) {
        const orgRobotIds = robots.map(r => r.id);
        filteredJobs = jobsRes.data.filter(job => orgRobotIds.includes(job.robot));
      }
      setJobs(filteredJobs);
      
      if (showDetailModal) {
        const updatedJob = jobsRes.data.find(j => j.id === showDetailModal.id);
        if (updatedJob) setShowDetailModal(updatedJob);
      }
    } catch (err) {
      setError(t('jobs.error_operation_failed', 'İşlem başarısız.'));
    }
  };

  const resetScheduleForm = () => {
    setScheduleForm({
      name: '',
      schedule_type: 'once',
      run_at: '',
      run_time: '09:00',
      weekdays: [],
      monthly_option: 'day_of_month',
      day_of_month: 1,
      weekday_of_month: 0,
      repeat_interval: 60,
      cron_expression: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      is_active: true
    });
  };

  const toggleWeekday = (day) => {
    if (scheduleForm.weekdays.includes(day)) {
      setScheduleForm({ ...scheduleForm, weekdays: scheduleForm.weekdays.filter(d => d !== day) });
    } else {
      setScheduleForm({ ...scheduleForm, weekdays: [...scheduleForm.weekdays, day].sort() });
    }
  };

  const selectAllWeekdays = () => {
    setScheduleForm({ ...scheduleForm, weekdays: [0, 1, 2, 3, 4] });
  };

  const clearWeekdays = () => {
    setScheduleForm({ ...scheduleForm, weekdays: [] });
  };

  const handleRun = async (jobId) => {
    try {
      await API.post(`/jobs/${jobId}/run/`);
      setSuccess(t('jobs.success_job_started', 'İş başlatıldı!'));
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || t('jobs.error_job_start_failed', 'İş başlatılamadı.'));
    }
  };

  const handleTogglePause = async (job) => {
    try {
      await API.patch(`/jobs/${job.id}/`, { is_paused: !job.is_paused });
      setSuccess(job.is_paused ? t('jobs.success_job_activated', 'İş aktif edildi.') : t('jobs.success_job_paused', 'İş duraklatıldı.'));
      fetchData();
    } catch (err) {
      setError(t('jobs.error_action_failed', 'İşlem başarısız oldu.'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('jobs.confirm_delete_job', 'Bu işi silmek istediğinize emin misiniz?'))) {
      try {
        await API.delete(`/jobs/${id}/`);
        setSuccess(t('jobs.success_job_deleted', 'İş silindi.'));
        fetchData();
      } catch (err) {
        setError(t('jobs.error_delete_failed', 'Silme işlemi başarısız oldu.'));
      }
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      name: job.name,
      description: job.description || '',
      department: job.department || '',
      script_path: job.script_path,
      robot: job.robot,
      manual_time_minutes: job.manual_time_minutes || 15,
      is_paused: job.is_paused || false
    });
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingJob(null);
    setFormData({ name: '', description: '', department: '', script_path: '', robot: '', manual_time_minutes: 15, is_paused: false });
    setShowModal(true);
  };

  // Script dosyası yükleme handler
  const handleScriptUpload = async (jobId, file) => {
    if (!file) return;

    if (!file.name.endsWith('.py')) {
      setError(t('jobs.error_only_python_files', 'Sadece .py dosyaları yüklenebilir.'));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await API.post(`/jobs/${jobId}/upload_script/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(`${t('jobs.success_script_uploaded', 'Script yüklendi:')} ${res.data.script_name}`);
      // Detay modalını güncelle
      const jobsRes = await API.get(`/jobs/?_t=${Date.now()}`);
      let filteredJobs = jobsRes.data;
      if (activeOrg) {
        const robotsRes = await API.get(`/robots/?_t=${Date.now()}`);
        const filteredRobots = robotsRes.data.filter(robot =>
          robot.organization === activeOrg.id || robot.organization_name === activeOrg.name
        );
        const orgRobotIds = filteredRobots.map(r => r.id);
        filteredJobs = jobsRes.data.filter(job => orgRobotIds.includes(job.robot));
      }
      setJobs(filteredJobs);
      const updatedJob = filteredJobs.find(j => j.id === jobId);
      if (updatedJob) setShowDetailModal(updatedJob);
    } catch (err) {
      setError(err.response?.data?.error || t('jobs.error_script_upload_failed', 'Script yükleme başarısız.'));
    }
  };

  const handleStopProcess = async (logId) => {
    if (!window.confirm(t('jobs.confirm_stop_process', 'Bu süreci durdurmak istediğinize emin misiniz?'))) {
      return;
    }
    try {
      await API.post(`/logs/${logId}/stop_log/`);
      setSuccess(t('jobs.success_stop_requested', 'Süreç durdurma talebi iletildi.'));
      
      const jobsRes = await API.get(`/jobs/?_t=${Date.now()}`);
      let filteredJobs = jobsRes.data;
      if (activeOrg) {
        const robotsRes = await API.get(`/robots/?_t=${Date.now()}`);
        const filteredRobots = robotsRes.data.filter(robot =>
          robot.organization === activeOrg.id || robot.organization_name === activeOrg.name
        );
        const orgRobotIds = filteredRobots.map(r => r.id);
        filteredJobs = jobsRes.data.filter(job => orgRobotIds.includes(job.robot));
      }
      setJobs(filteredJobs);
      if (showDetailModal) {
        const updatedJob = filteredJobs.find(j => j.id === showDetailModal.id);
        if (updatedJob) setShowDetailModal(updatedJob);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('jobs.error_stop_failed', 'Süreç durdurulamadı.'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString(i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US');
  };

  const getStatusBadge = (job) => {
    if (job.is_paused) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 flex items-center w-fit"><Pause size={12} className="mr-1" /> {t('jobs.status_paused', 'Duraklatıldı')}</span>;
    }
    return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 flex items-center w-fit"><CheckCircle size={12} className="mr-1" /> {t('jobs.status_active', 'Aktif')}</span>;
  };

  const getLogStatusBadge = (status) => {
    switch (status) {
      case 'success': return <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-800">{t('common.success', 'Başarılı')}</span>;
      case 'error': return <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-800">{t('common.error', 'Hata')}</span>;
      case 'running': return <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">{t('jobs.log_status_running', 'Çalışıyor')}</span>;
      case 'queued': return <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-800">{t('jobs.log_status_queued', 'Kuyrukta')}</span>;
      default: return <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getScheduleTypeLabel = (type) => {
    const labels = {
      'once': t('jobs.schedule_once', 'Bir Kez'),
      'daily': t('jobs.schedule_daily', 'Günlük'),
      'weekly': t('jobs.schedule_weekly', 'Haftalık'),
      'monthly': t('jobs.schedule_monthly', 'Aylık'),
      'interval': t('jobs.schedule_interval', 'Belirli Aralıkla'),
      'cron': t('jobs.schedule_cron', 'Özel (Cron)')
    };
    return labels[type] || type;
  };

  const isSuperuser = currentUser?.is_superuser;
  const isOrgAdmin = activeOrg && (activeOrg.user_role === 'owner' || activeOrg.user_role === 'admin');
  const canManage = isSuperuser || isOrgAdmin;
  const canStopProcesses = currentUser?.is_superuser || currentUser?.is_staff;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('jobs.title', 'Süreçler')}</h1>
          <p className="text-gray-500 mt-1">{t('jobs.subtitle', 'Robot görevlerinin yönetimi ve takibi')}</p>
        </div>
        {canManage && (
          <button
            onClick={openAddModal}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={18} className="mr-2" />
            {t('jobs.new_job', 'Yeni Süreç Ekle')}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between text-red-700">
          <div className="flex items-center"><AlertTriangle size={20} className="mr-2" />{error}</div>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between text-green-700">
          <div className="flex items-center"><CheckCircle size={20} className="mr-2" />{success}</div>
          <button onClick={() => setSuccess(null)}><X size={18} /></button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">{t('common.loading', 'Yükleniyor...')}</div>
        ) : jobs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('jobs.no_jobs_found', 'Henüz hiç iş tanımlanmamış.')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('jobs.process_name', 'Süreç Adı')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('jobs.script', 'Script')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('jobs.robot', 'Robot')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('jobs.schedule', 'Zamanlama')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.status', 'Durum')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions', 'İşlemler')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map((job) => {
                  const robot = robots.find(r => r.id === job.robot);
                  return (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => setShowDetailModal(job)}
                          className="text-left hover:text-blue-600"
                        >
                          <div className="text-sm font-medium text-gray-900">{job.name}</div>
                          <div className="text-xs text-gray-500">
                            {job.department && <span className="mr-2 px-1.5 py-0.5 text-xs font-semibold rounded bg-blue-50 text-blue-700">{job.department}</span>}
                            {job.description || '-'}
                          </div>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono text-xs" title={job.script_path}>
                        {getShortenedPath(job.script_path)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Bot size={14} className="mr-1 text-blue-500" />
                          {robot?.name || `Robot #${job.robot}`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => setShowScheduleModal(job)}
                          className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Calendar size={14} className="mr-1" />
                          {t('jobs.schedules_count', '{{count}} zamanlama', { count: job.schedules?.length || 0 })}
                          <Plus size={12} className="ml-1" />
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(job)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-1">
                        <button
                          onClick={() => handleRun(job.id)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          title={t('jobs.run_now', 'Hemen Çalıştır')}
                        >
                          <Play size={16} />
                        </button>
                        {canManage && (
                          <>
                            <button
                              onClick={() => handleTogglePause(job)}
                              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
                              title={job.is_paused ? t('jobs.activate', 'Aktif Et') : t('jobs.pause', 'Duraklat')}
                            >
                              {job.is_paused ? <Play size={16} /> : <Pause size={16} />}
                            </button>
                            <button
                              onClick={() => handleEdit(job)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title={t('common.edit', 'Düzenle')}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(job.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title={t('common.delete', 'Sil')}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Job Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">{showDetailModal.name}</h2>
              <button onClick={() => setShowDetailModal(null)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg col-span-2 flex justify-between items-center">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t('jobs.department', 'Departman')}</p>
                    <p className="font-medium text-blue-700">{showDetailModal.department || t('jobs.not_specified', 'Belirtilmemiş')}</p>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1 flex items-center"><FileText size={12} className="mr-1" /> {t('jobs.script_path', 'Script Yolu')}</p>
                  <p className="font-mono text-sm">{showDetailModal.script_path || t('jobs.not_uploaded_yet', 'Henüz yüklenmedi')}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1 flex items-center"><Bot size={12} className="mr-1" /> {t('jobs.robot', 'Robot')}</p>
                  <p className="font-medium">{robots.find(r => r.id === showDetailModal.robot)?.name || '-'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1 flex items-center"><Clock size={12} className="mr-1" /> {t('jobs.manual_time', 'Manuel Süre')}</p>
                  <p className="font-medium">{t('jobs.minutes_count', '{{count}} dakika', { count: showDetailModal.manual_time_minutes || 0 })}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">{t('common.status', 'Durum')}</p>
                  {getStatusBadge(showDetailModal)}
                </div>
              </div>

              {/* Script Upload Section */}
              {canManage && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <Upload size={16} className="mr-2" /> {t('jobs.upload_script', 'Script Yükle')}
                  </h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-700 mb-3">
                      {t('jobs.upload_script_desc', 'Python script dosyası (.py) yükleyerek bu iş için çalıştırılacak kodu belirleyebilirsiniz.')}
                    </p>
                    <input
                      type="file"
                      accept=".py"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleScriptUpload(showDetailModal.id, e.target.files[0]);
                          e.target.value = ''; // Reset input
                        }
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                    />
                    {showDetailModal.script_name && (
                      <div className="mt-3 flex items-center text-sm text-green-700 bg-green-50 p-2 rounded">
                        <CheckCircle size={14} className="mr-2" />
                        {t('jobs.current_script', 'Mevcut script:')} {showDetailModal.script_name}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {showDetailModal.description && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t('jobs.description_label', 'Açıklama')}</h3>
                  <p className="text-gray-600">{showDetailModal.description}</p>
                </div>
              )}

              {/* Zamanlamalar */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <Calendar size={16} className="mr-2" /> {t('jobs.schedules_title', 'Zamanlamalar ({{count}})', { count: showDetailModal.schedules?.length || 0 })}
                  </h3>
                  {canManage && (
                    <button
                      onClick={() => { setShowDetailModal(null); setShowScheduleModal(showDetailModal); }}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <Plus size={14} className="mr-1" /> {t('jobs.new_schedule', 'Yeni Zamanlama')}
                    </button>
                  )}
                </div>
                {showDetailModal.schedules && showDetailModal.schedules.length > 0 ? (
                  <div className="space-y-2">
                    {showDetailModal.schedules.map(schedule => (
                      <div key={schedule.id} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                        <div className="flex-1">
                          <p className="font-medium">{schedule.name || getScheduleTypeLabel(schedule.schedule_type)}</p>
                          <p className="text-xs text-gray-500">
                            {schedule.schedule_type === 'once' && formatDate(schedule.run_at)}
                            {schedule.schedule_type === 'daily' && t('jobs.schedule_desc_daily', 'Her gün saat {{time}}', { time: schedule.run_time })}
                            {schedule.schedule_type === 'weekly' && t('jobs.schedule_desc_weekly', 'Haftalık: {{days}} - {{time}}', { days: schedule.weekdays?.map(d => WEEKDAYS[d]?.label).join(', '), time: schedule.run_time })}
                            {schedule.schedule_type === 'monthly' && (() => {
                              const monthlyLabels = {
                                'day_of_month': t('jobs.day_of_month_desc', 'Ayın {{day}}. günü', { day: schedule.day_of_month }),
                                'first_day': t('jobs.first_day_desc', 'Ayın ilk günü'),
                                'last_day': t('jobs.last_day_desc', 'Ayın son günü'),
                                'first_business': t('jobs.first_business_desc', 'Ayın ilk iş günü'),
                                'last_business': t('jobs.last_business_desc', 'Ayın son iş günü'),
                                'first_weekday': t('jobs.first_weekday_desc', 'Ayın ilk {{weekday}}', { weekday: WEEKDAYS[schedule.weekday_of_month]?.fullLabel }),
                                'last_weekday': t('jobs.last_weekday_desc', 'Ayın son {{weekday}}', { weekday: WEEKDAYS[schedule.weekday_of_month]?.fullLabel })
                              };
                              return `${monthlyLabels[schedule.monthly_option] || t('jobs.schedule_monthly', 'Aylık')} - ${schedule.run_time}`;
                            })()}
                            {schedule.schedule_type === 'interval' && t('jobs.interval_desc', 'Her {{minutes}} dakikada', { minutes: schedule.repeat_interval })}
                            {schedule.schedule_type === 'cron' && schedule.cron_expression}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {canManage ? (
                            <>
                              <button
                                onClick={() => handleToggleSchedule(schedule)}
                                className={`px-2 py-1 text-xs rounded ${schedule.is_active ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                              >
                                {schedule.is_active ? t('jobs.status_active', 'Aktif') : t('jobs.status_paused', 'Pasif')}
                              </button>
                              <button
                                onClick={() => handleDeleteSchedule(schedule.id)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title={t('common.delete', 'Sil')}
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          ) : (
                            <span className={`px-2 py-1 text-xs rounded ${schedule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {schedule.is_active ? t('jobs.status_active', 'Aktif') : t('jobs.status_paused', 'Pasif')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">{t('jobs.no_schedules_found', 'Henüz zamanlama eklenmemiş.')}</p>
                )}
              </div>

              {/* Loglar */}
              {showDetailModal.logs && showDetailModal.logs.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">{t('jobs.recent_logs', 'Son Loglar')} ({showDetailModal.logs.length})</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {showDetailModal.logs.slice(0, 10).map(log => (
                      <div key={log.id} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-gray-500">{formatDate(log.started_at)}</span>
                          <div className="flex items-center gap-2">
                            {log.status === 'running' && canStopProcesses && (
                              <button
                                onClick={() => handleStopProcess(log.id)}
                                className="px-2 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-800 hover:bg-red-200 border border-red-200 transition-colors"
                              >
                                {t('jobs.stop_process', 'Süreci Durdur')}
                              </button>
                            )}
                            {getLogStatusBadge(log.status)}
                          </div>
                        </div>
                        {log.message && (
                          <p className="text-sm text-gray-600">{log.message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-xs text-gray-400 pt-4 border-t">
                {t('jobs.created_at', 'Oluşturulma:')} {formatDate(showDetailModal.created_at)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{t('jobs.add_schedule', 'Zamanlama Ekle')}</h2>
                <p className="text-sm text-gray-500">{showScheduleModal.name}</p>
              </div>
              <button onClick={() => { setShowScheduleModal(null); resetScheduleForm(); }} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-5">
              {/* Zamanlama Adı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.schedule_name_optional', 'Zamanlama Adı (opsiyonel)')}</label>
                <input
                  type="text"
                  value={scheduleForm.name}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={t('jobs.morning_report_placeholder', 'Sabah raporu')}
                />
              </div>

              {/* Zamanlama Türü */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('jobs.schedule_type', 'Zamanlama Türü')}</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'once', label: t('jobs.schedule_once', 'Bir Kez'), icon: '📅' },
                    { value: 'daily', label: t('jobs.schedule_daily', 'Günlük'), icon: '☀️' },
                    { value: 'weekly', label: t('jobs.schedule_weekly', 'Haftalık'), icon: '📆' },
                    { value: 'monthly', label: t('jobs.schedule_monthly', 'Aylık'), icon: '🗓️' },
                    { value: 'interval', label: t('jobs.schedule_interval', 'Aralıklı'), icon: '🔄' },
                    { value: 'cron', label: t('jobs.schedule_cron', 'Cron'), icon: '⚙️' },
                  ].map(type => (
                    <button
                      key={type.value}
                      onClick={() => setScheduleForm({ ...scheduleForm, schedule_type: type.value })}
                      className={`p-3 rounded-lg border text-center transition-all ${scheduleForm.schedule_type === type.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="text-xl mb-1">{type.icon}</div>
                      <div className="text-xs font-medium">{type.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Bir Kez - Tarih/Saat */}
              {scheduleForm.schedule_type === 'once' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.date_and_time', 'Tarih ve Saat')}</label>
                  <input
                    type="datetime-local"
                    value={scheduleForm.run_at}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, run_at: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Günlük / Haftalık / Aylık - Saat */}
              {['daily', 'weekly', 'monthly'].includes(scheduleForm.schedule_type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.run_time_label', 'Çalışma Saati')}</label>
                  <input
                    type="time"
                    value={scheduleForm.run_time}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, run_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Haftalık - Gün Seçimi */}
              {scheduleForm.schedule_type === 'weekly' && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">{t('jobs.days', 'Günler')}</label>
                    <div className="space-x-2">
                      <button
                        onClick={selectAllWeekdays}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {t('jobs.weekdays_btn', 'Hafta içi')}
                      </button>
                      <button
                        onClick={clearWeekdays}
                        className="text-xs text-gray-600 hover:text-gray-800"
                      >
                        {t('common.clear', 'Temizle')}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {WEEKDAYS.map(day => (
                      <button
                        key={day.value}
                        onClick={() => toggleWeekday(day.value)}
                        className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${scheduleForm.weekdays.includes(day.value)
                          ? 'border-blue-500 bg-blue-600 text-white'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Aylık - Seçenekler */}
              {scheduleForm.schedule_type === 'monthly' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('jobs.monthly_option', 'Aylık Seçenek')}</label>
                    <div className="space-y-2">
                      <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="radio"
                          name="monthly_option"
                          value="day_of_month"
                          checked={scheduleForm.monthly_option === 'day_of_month'}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, monthly_option: e.target.value })}
                          className="mr-3"
                        />
                        <span className="flex-1">{t('jobs.specific_day_of_month', 'Ayın belirli günü')}</span>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          value={scheduleForm.day_of_month}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_month: parseInt(e.target.value) })}
                          className="w-16 px-2 py-1 border rounded text-center"
                          disabled={scheduleForm.monthly_option !== 'day_of_month'}
                        />
                      </label>
                      <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="radio"
                          name="monthly_option"
                          value="first_day"
                          checked={scheduleForm.monthly_option === 'first_day'}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, monthly_option: e.target.value })}
                          className="mr-3"
                        />
                        <span>{t('jobs.first_day_of_month', 'Ayın ilk günü')}</span>
                      </label>
                      <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="radio"
                          name="monthly_option"
                          value="last_day"
                          checked={scheduleForm.monthly_option === 'last_day'}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, monthly_option: e.target.value })}
                          className="mr-3"
                        />
                        <span>{t('jobs.last_day_of_month', 'Ayın son günü')}</span>
                      </label>
                      <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="radio"
                          name="monthly_option"
                          value="first_business"
                          checked={scheduleForm.monthly_option === 'first_business'}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, monthly_option: e.target.value })}
                          className="mr-3"
                        />
                        <span>{t('jobs.first_business_day', 'Ayın ilk iş günü')}</span>
                      </label>
                      <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="radio"
                          name="monthly_option"
                          value="last_business"
                          checked={scheduleForm.monthly_option === 'last_business'}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, monthly_option: e.target.value })}
                          className="mr-3"
                        />
                        <span>{t('jobs.last_business_day', 'Ayın son iş günü')}</span>
                      </label>
                      <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="radio"
                          name="monthly_option"
                          value="first_weekday"
                          checked={scheduleForm.monthly_option === 'first_weekday'}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, monthly_option: e.target.value })}
                          className="mr-3"
                        />
                        <span className="flex-1">{t('jobs.first_weekday_of_month', 'Ayın ilk')}</span>
                        <select
                          value={scheduleForm.weekday_of_month}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, weekday_of_month: parseInt(e.target.value) })}
                          className="px-2 py-1 border rounded"
                          disabled={scheduleForm.monthly_option !== 'first_weekday'}
                        >
                          {WEEKDAYS.map(day => (
                            <option key={day.value} value={day.value}>{day.fullLabel}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Aralıklı */}
              {scheduleForm.schedule_type === 'interval' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.repeat_interval_minutes', 'Tekrar Aralığı (dakika)')}</label>
                  <div className="flex gap-2">
                    {[15, 30, 60, 120, 240].map(mins => (
                      <button
                        key={mins}
                        onClick={() => setScheduleForm({ ...scheduleForm, repeat_interval: mins })}
                        className={`px-3 py-2 rounded-lg border text-sm ${scheduleForm.repeat_interval === mins
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200'
                          }`}
                      >
                        {mins < 60 ? `${mins}dk` : `${mins / 60}sa`}
                      </button>
                    ))}
                    <input
                      type="number"
                      value={scheduleForm.repeat_interval}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, repeat_interval: parseInt(e.target.value) })}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg"
                      min="1"
                    />
                  </div>
                </div>
              )}

              {/* Cron */}
              {scheduleForm.schedule_type === 'cron' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.cron_expression', 'Cron İfadesi')}</label>
                  <input
                    type="text"
                    value={scheduleForm.cron_expression}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, cron_expression: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono"
                    placeholder="0 9 * * 1-5"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('jobs.cron_format_help', 'Format: dakika saat gün ay haftanın-günü')}</p>
                  <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                    <p className="font-medium mb-1">{t('jobs.examples', 'Örnekler:')}</p>
                    <p><code className="bg-gray-200 px-1 rounded">0 9 * * 1-5</code> - {t('jobs.cron_example_1', 'Hafta içi her gün saat 09:00')}</p>
                    <p><code className="bg-gray-200 px-1 rounded">0 8,18 * * *</code> - {t('jobs.cron_example_2', 'Her gün 08:00 ve 18:00')}</p>
                    <p><code className="bg-gray-200 px-1 rounded">0 0 1 * *</code> - {t('jobs.cron_example_3', 'Her ayın 1\'i gece yarısı')}</p>
                  </div>
                </div>
              )}

              {/* Başlangıç/Bitiş Tarihi */}
              {scheduleForm.schedule_type !== 'once' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.start_date', 'Başlangıç Tarihi')}</label>
                    <input
                      type="date"
                      value={scheduleForm.start_date}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.end_date_optional', 'Bitiş Tarihi (opsiyonel)')}</label>
                    <input
                      type="date"
                      value={scheduleForm.end_date}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, end_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Aktif/Pasif */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="schedule_active"
                  checked={scheduleForm.is_active}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, is_active: e.target.checked })}
                  className="rounded text-blue-600 focus:ring-blue-500 mr-2"
                />
                <label htmlFor="schedule_active" className="text-sm text-gray-700">{t('jobs.create_active_schedule', 'Zamanlamayı aktif olarak oluştur')}</label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => { setShowScheduleModal(null); resetScheduleForm(); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.cancel', 'İptal')}
              </button>
              <button
                onClick={handleScheduleSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <Calendar size={18} className="mr-2" />
                {t('jobs.save_schedule', 'Zamanlamayı Kaydet')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Job Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingJob ? t('jobs.edit_job', 'İş Düzenle') : t('jobs.add_job', 'Yeni İş Ekle')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.job_name_required', 'İş Adı *')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={t('jobs.daily_report_placeholder', 'Günlük Rapor')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.department', 'Departman')}</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={t('jobs.department_placeholder', 'Örn: Muhasebe, İnsan Kaynakları')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.description_label', 'Açıklama')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={t('jobs.description_placeholder', 'Günlük raporu hazırlar')}
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Upload size={14} className="inline mr-1" /> {t('jobs.python_script_file', 'Python Script Dosyası')}
                </label>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <input
                    type="file"
                    accept=".py"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        setScriptFile(e.target.files[0]);
                        // Script path'i dosya adıyla güncelle
                        setFormData({ ...formData, script_path: e.target.files[0].name });
                      }
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                  />
                  {scriptFile && (
                    <div className="mt-2 flex items-center text-sm text-green-700">
                      <CheckCircle size={14} className="mr-1" />
                      {t('jobs.selected_script', 'Seçili:')} {scriptFile.name}
                    </div>
                  )}
                  {!scriptFile && editingJob?.script_name && (
                    <div className="mt-2 text-sm text-gray-500">
                      {t('jobs.current_script', 'Mevcut script:')} {editingJob.script_name}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.robot_required_label', 'Robot *')}</label>
                <select
                  value={formData.robot}
                  onChange={(e) => setFormData({ ...formData, robot: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('jobs.select_robot', 'Robot seçin')}</option>
                  {robots.map(r => (
                    <option key={r.id} value={r.id}>{r.name} ({r.hostname})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('jobs.manual_time_minutes_label', 'Manuel Süre (dk)')}</label>
                <input
                  type="number"
                  value={formData.manual_time_minutes}
                  onChange={(e) => setFormData({ ...formData, manual_time_minutes: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="15"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">{t('jobs.manual_time_help', 'Bu işin manuel yapılması kaç dakika sürer? (ROI hesabı için)')}</p>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_paused"
                  checked={formData.is_paused}
                  onChange={(e) => setFormData({ ...formData, is_paused: e.target.checked })}
                  className="rounded text-blue-600 focus:ring-blue-500 mr-2"
                />
                <label htmlFor="is_paused" className="text-sm text-gray-700">{t('jobs.create_paused', 'Duraklatılmış olarak oluştur')}</label>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-xs text-blue-700">
                💡 <strong>{t('jobs.create_paused_title', 'Duraklatılmış olarak oluştur:')}</strong> {t('jobs.create_paused_help', 'İş tanımlanır ama otomatik çalışmaz. Test edilip onaylanınca aktif edilir.')}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowModal(false); setEditingJob(null); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.cancel', 'İptal')}
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {editingJob ? t('common.save', 'Kaydet') : t('common.add', 'Ekle')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
